// Data-source-driven derivation for the Visual designer.
//
// The visual's columns and criteria are no longer hand-maintained lists —
// they are DERIVED from whichever data source the visual uses:
//
//   • SQL mode    → SELECT aliases become the available columns; `?.var`
//                   placeholders become the criteria (re-derived live as the
//                   SQL is edited, preserving any metadata the user already
//                   set on a placeholder of the same name).
//   • Schema mode → the referenced Visual Schema's table columns become the
//                   available columns (a draggable tree); criteria are built
//                   visually by dragging a schema column into the criteria
//                   drop-zone.
//
// This mirrors the insight-bi Angular designer (schema treeview → drag a
// column to a group / filter) while keeping the runtime JSON contract intact:
//   - Both modes populate SQLMODEL.SQLModel.COLUMNS from the layout groups —
//     in schema mode the engine builds the SELECT + JOINs from that master
//     list, and in SQL mode (where the query is the author's own) it is still
//     the visual's column bank, which the app server resolves a pivot's or a
//     feed's placements through.
//   - SQL mode also writes CRITERIA.rules so the engine can attach a label /
//     type / default to each `?.var` prompt.

import type {
  VisualModel, VisualColumn, CriteriaNode, CriteriaRule, VisualSchema,
  SchemaTableColumn,
} from './types'
import { criteriaLeaves } from './types'
import type { DisplayFormat } from '@twasta/page-runtime/txn'
import { blankColumn, blankCriteriaRule } from './helpers'
import { parseSourceSql, variableLabel, buildBindingSuffix } from './sqlParser'

export type ColType = 'string' | 'number' | 'date'

/** A column offered in the Layout "available columns" bank / schema tree.
 *  Not yet a VisualColumn — it is materialised on first placement. */
export interface BankColumn {
  /** Stable identity used to tell placed vs unplaced apart. */
  key: string
  /** Human label shown on the chip. */
  label: string
  /** SELECT alias (SQL mode) or `${DBTABLE}_${DBNAME}` (schema mode) — the
   *  NAME a materialised column / layout echo carries. */
  name: string
  /** Source table (schema mode only). */
  DBTABLE?: string
  /** Source column (schema mode only). */
  DBNAME?: string
  /** Owning table display (schema mode — used to group the tree). */
  tableLabel?: string
  type: ColType
  COLTYPE: 'CHAR' | 'NUMBER' | 'DATE'
  /** Default aggregation suggested by the schema (schema mode). */
  defaultFunction?: string
  /** Rich display format authored on the schema folder column (schema mode) —
   *  carried onto the materialised VisualColumn so the visual inherits it. */
  display?: DisplayFormat
  /** True when the SQL expression is wrapped in an aggregate (SQL mode). */
  isAggregate?: boolean
}

const norm = (s: string | null | undefined) =>
  (s || '').trim().replace(/\s+/g, '_').toUpperCase()

function coltypeOf(t: ColType): 'CHAR' | 'NUMBER' | 'DATE' {
  return t === 'number' ? 'NUMBER' : t === 'date' ? 'DATE' : 'CHAR'
}

function javatypeOf(t: ColType): string {
  return t === 'number'
    ? 'java.math.BigDecimal'
    : t === 'date'
    ? 'java.sql.Date'
    : 'java.lang.String'
}

function typeFromColtype(ct: string | undefined): ColType {
  const c = (ct || '').toUpperCase()
  if (c === 'NUMBER') return 'number'
  if (c === 'DATE') return 'date'
  return 'string'
}

// ── Identity ──────────────────────────────────────────────────────
// The key used to match a placed layout column back to a bank column.
//   schema mode → DBTABLE.DBNAME   (the SELECT is generated from these)
//   sql mode    → NAME (the SELECT alias)

export function bankKeyOf(col: { DBTABLE?: string; DBNAME?: string; NAME?: string }, mode: string): string {
  if (mode === 'sqlmodel' && (col.DBTABLE || col.DBNAME)) {
    return `${norm(col.DBTABLE)}.${norm(col.DBNAME)}`
  }
  return norm(col.NAME)
}

// ── Available columns (the bank / schema tree) ────────────────────

/** A result column the DATABASE reported for a SQL-mode query
 *  (`POST /visual-data/sql-columns`) — authoritative, unlike the regex parse. */
export interface ResolvedSqlColumn {
  name: string
  type: string
  coltype: string
}

/** All columns a visual could use, derived from its data source.
 *
 *  ``resolved`` (SQL mode) are the columns the database reported for the
 *  current SOURCE_SQL. They win over the regex parse because that parse cannot
 *  expand `SELECT *` — it only sees a literal star. Absent (still loading, or
 *  the query errored) the parse is the fallback so the bank stays live while
 *  the author types. */
export function availableColumns(
  model: VisualModel,
  schema: VisualSchema | null,
  resolved?: ResolvedSqlColumn[] | null,
): BankColumn[] {
  const mode = model.DATA_SOURCE_MODE || 'sql'
  if (mode === 'sqlmodel') return schemaColumns(schema)
  if (resolved && resolved.length) return resolvedSqlColumns(resolved)
  return sqlColumns(model.SOURCE_SQL || '')
}

function resolvedSqlColumns(resolved: ResolvedSqlColumn[]): BankColumn[] {
  return resolved.map((c) => {
    const type: ColType = c.coltype === 'NUMBER' ? 'number'
      : c.coltype === 'DATE' ? 'date' : 'string'
    return {
      key: norm(c.name),
      label: c.name,
      name: c.name.toUpperCase(),
      type,
      COLTYPE: coltypeOf(type),
      // The DB reports a result column, not an expression — aggregation is a
      // choice the author makes on the placed column.
      isAggregate: false,
    }
  })
}

function sqlColumns(sourceSql: string): BankColumn[] {
  const parsed = parseSourceSql(sourceSql)
  return parsed.columns.map((c) => ({
    key: norm(c.alias),
    label: c.alias,
    name: c.alias.toUpperCase(),
    type: c.inferredType,
    COLTYPE: coltypeOf(c.inferredType),
    isAggregate: c.isAggregate,
  }))
}

function schemaColumns(schema: VisualSchema | null): BankColumn[] {
  if (!schema || !Array.isArray(schema.TABLES)) return []

  // Index the source columns so folder references resolve to real columns
  // (and inherit HIDDEN / COLTYPE / DEFAULTFUNCTION the folder omits).
  const sourceByKey = new Map<string, { col: SchemaTableColumn; table: string; tableLabel: string }>()
  for (const tbl of schema.TABLES) {
    const table = tbl.TABLE_NAME || ''
    const tableLabel = tbl.TABLE_DISPLAY_NAME || table
    for (const col of tbl.COLUMNS || []) {
      sourceByKey.set(`${norm(table)}.${norm(col.DBNAME)}`, { col, table, tableLabel })
    }
  }

  const toBank = (
    table: string, dbname: string, groupLabel: string,
    display?: string, coltype?: string, defaultFn?: string,
    format?: DisplayFormat,
    uniqueName?: string,
  ): BankColumn => {
    const t = typeFromColtype(coltype)
    return {
      key: `${norm(table)}.${norm(dbname)}`,
      label: display || dbname,
      // The SELECT alias. The schema's own UNIQUE_NAME wins (the author may
      // have edited it); otherwise it falls back to the same TABLE_COLUMN this
      // has always derived, so schemas written before UNIQUE_NAME existed keep
      // producing exactly the aliases their visuals already reference.
      name: norm(uniqueName || '') || `${norm(table)}_${norm(dbname)}`,
      DBTABLE: table.toUpperCase(),
      DBNAME: (dbname || '').toUpperCase(),
      tableLabel: groupLabel,
      type: t,
      COLTYPE: coltypeOf(t),
      defaultFunction: defaultFn || '',
      display: format,
    }
  }

  const out: BankColumn[] = []
  const seen = new Set<string>()

  // Business-model folders (Facts / Dimensions / custom) come first — this is
  // the tree the user drags from. Each folder column references a source
  // column; a reference to a hidden or unknown source column is skipped.
  for (const grp of schema.COLUMN_GROUPS || []) {
    const groupLabel = [grp.GROUP_ICON, grp.GROUP_DISPLAY_NAME || grp.GROUP_NAME].filter(Boolean).join(' ') || 'Columns'
    for (const ref of grp.COLUMNS || []) {
      const key = `${norm(ref.TABLE_NAME)}.${norm(ref.DBNAME)}`
      if (seen.has(key)) continue
      const src = sourceByKey.get(key)
      if (src?.col.HIDDEN) continue
      seen.add(key)
      out.push(toBank(
        ref.TABLE_NAME || src?.table || '',
        ref.DBNAME || '',
        groupLabel,
        ref.DISPLAY_NAME || src?.col.DISPLAY_NAME,
        ref.COLTYPE || src?.col.COLTYPE,
        ref.DEFAULTFUNCTION || src?.col.DEFAULTFUNCTION,
        ref.display,
        (ref.UNIQUE_NAME as string) || (src?.col.UNIQUE_NAME as string) || '',
      ))
    }
  }

  // Source columns no folder references stay reachable, grouped by table.
  for (const tbl of schema.TABLES) {
    const table = tbl.TABLE_NAME || ''
    const tableLabel = tbl.TABLE_DISPLAY_NAME || table
    for (const col of tbl.COLUMNS || []) {
      const key = `${norm(table)}.${norm(col.DBNAME)}`
      if (col.HIDDEN || seen.has(key)) continue
      seen.add(key)
      out.push(toBank(
        table, col.DBNAME || '', tableLabel,
        col.DISPLAY_NAME, col.COLTYPE, col.DEFAULTFUNCTION,
        undefined,
        (col.UNIQUE_NAME as string) || '',
      ))
    }
  }
  return out
}

/** Group a flat bank-column list by its owning table (for the schema tree). */
export function groupByTable(cols: BankColumn[]): { table: string; columns: BankColumn[] }[] {
  const order: string[] = []
  const map = new Map<string, BankColumn[]>()
  for (const c of cols) {
    const t = c.tableLabel || 'Columns'
    if (!map.has(t)) { map.set(t, []); order.push(t) }
    map.get(t)!.push(c)
  }
  return order.map((t) => ({ table: t, columns: map.get(t)! }))
}

// ── Materialise a bank column into a real VisualColumn ────────────

export function materializeColumn(bank: BankColumn, colId: number): VisualColumn {
  const col = blankColumn(colId)
  col.NAME = bank.name
  col.content = bank.label
  col.name = bank.label
  col.DBTABLE = bank.DBTABLE || ''
  col.tableName = bank.DBTABLE || ''
  col.DBNAME = bank.DBNAME || ''
  col.type = bank.type
  col.COLTYPE = bank.COLTYPE
  col.JAVATYPE = javatypeOf(bank.type)
  const fn = bank.isAggregate ? 'SUM' : (bank.defaultFunction || '')
  col.FUNCTION = fn
  col.DEFAULTFUNCTION = fn
  col.EXPRESSIONTYPE = fn ? 'G' : 'C'
  // Inherit the display format the schema author set on the folder column, so a
  // visual built on this schema is pre-formatted the moment the column lands.
  if (bank.display) col.display = bank.display
  return col
}

// ── Criteria derivation ───────────────────────────────────────────

export interface DerivedSqlCriteria {
  rules: CriteriaRule[]
  /** `?.@Token` system inputs found in the SQL — shown read-only (no prompt). */
  systemTokens: string[]
}

/** Re-derive the SQL-mode criteria set from `?.var` placeholders, preserving
 *  the label / type / operator / default the user already set on a placeholder
 *  of the same (normalised) name. Returned rules carry queryOption 'P' and a
 *  normalised UPPER_SNAKE `field` so the engine matches them to the SQL.
 *  SQL mode is FLAT — condition groups (schema-mode only) are flattened into
 *  their leaves for metadata carry-over. */
export function deriveSqlCriteria(
  sourceSql: string,
  existing: CriteriaNode[],
): DerivedSqlCriteria {
  const parsed = parseSourceSql(sourceSql)
  // A rule is found by the placeholder it configures: `promptVar` first (a
  // rule whose `field` names the filtered COLUMN, e.g. "orders.order_date",
  // links to `?.from_date` only through it), then `field`, then `id` — the
  // engine's own order, so the designer never drops a default the run uses.
  const byKey = new Map<string, CriteriaRule>()
  for (const r of criteriaLeaves(existing || [])) {
    for (const k of [norm(r.promptVar), norm(r.field), norm(r.id)]) {
      if (k && !byKey.has(k)) byKey.set(k, r)
    }
  }
  const seen = new Set<string>()
  const rules: CriteriaRule[] = []
  let n = 1
  for (const v of parsed.variables) {
    if (v.isSystem) continue
    const key = norm(v.name)
    if (!key || seen.has(key)) continue
    seen.add(key)
    const prev = byKey.get(key)
    const base = prev
      ? { ...prev, field: key, queryOption: 'P' }
      : {
          ...blankCriteriaRule(n),
          id: `Criteria_${n}`,
          field: key,
          promptLabel: variableLabel(v.name),
          queryOption: 'P',
        }
    // The placeholder's own `.<type>.<default>` binding is authoritative for
    // SQL mode (it's what the engine reads), so reflect it onto the rule.
    if (v.type) { base.COLTYPE = v.type; base.currentFieldType = v.type }
    if (v.default !== undefined) base.value = v.default
    rules.push(base)
    n += 1
  }
  const systemTokens = parsed.variables
    .filter((v) => v.isSystem)
    .map((v) => v.name)
    .filter((name, i, arr) => arr.indexOf(name) === i)
  return { rules, systemTokens }
}

/** Rewrite every `?.<name>` placeholder for `varName` in the SQL so it carries
 *  the insight-bi binding `?.<name>.<type>.<default>`. Matching is
 *  case-insensitive on the name; the author's original casing is preserved.
 *  Clearing both type and default reverts the placeholder to a bare `?.<name>`. */
export function setSqlBinding(
  sourceSql: string,
  varName: string,
  type: ColType | undefined,
  defaultValue: string | undefined,
): string {
  if (!sourceSql) return sourceSql
  const suffix = buildBindingSuffix(type, defaultValue)
  const safe = varName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  // ?.<name> followed by any existing dotted binding suffix.
  const re = new RegExp(`\\?\\.(${safe})((?:\\.[^\\s,()=<>!]+)+)?`, 'gi')
  return sourceSql.replace(re, (_m, name) => `?.${name}${suffix}`)
}

/** Build a criteria rule from a schema column dragged into the criteria
 *  drop-zone (schema mode). */
export function schemaColumnToCriteria(bank: BankColumn, n: number): CriteriaRule {
  return {
    ...blankCriteriaRule(n),
    id: `Criteria_${n}`,
    field: `${norm(bank.DBTABLE)}.${norm(bank.DBNAME)}`,
    DBNAME: bank.DBNAME || '',
    tableName: bank.DBTABLE || '',
    COLTYPE: bank.type,
    currentFieldType: bank.type,
    promptLabel: bank.label,
    operator: '=',
    queryOption: 'P',
  }
}

// ── Master/echo sync ──────────────────────────────────────────────

/** Keep SQLMODEL.SQLModel.COLUMNS — the visual's MASTER column list — equal to
 *  the union of the columns placed in the layout groups (deduped by COLID).
 *
 *  In schema mode the engine builds the SELECT from that list. In SQL mode it
 *  does not (the query is the author's own), and the list used to be EMPTIED
 *  here for that reason. It is still load-bearing: it is the visual's own
 *  column bank, and the app server resolves a row's placements through it. A
 *  grid or a card survives without one because it draws straight from the
 *  result set; a pivot and a feed do not, and deploy as a pivot that never
 *  finishes "analysing data" and a feed with nothing in it. The app server's
 *  own freehand visuals all carry one, built exactly this way
 *  (`generateSQLSeq()`), so SQL mode now keeps it too — every reader of the
 *  mode keys off DATA_SOURCE_MODE, which a saved model always carries, so a
 *  filled list cannot be mistaken for a schema source.
 *
 *  An ABSENT mode means schema, never SQL. Every other reader of this key
 *  already says so — `sanitizeVisualModel`'s detectMode falls through to
 *  'sqlmodel', and so does every backend and runtime reader
 *  (`visual_data_service`, the deployers, `bi_verify`). */
export function syncMasterFromGroups(m: VisualModel): void {
  const seen = new Map<number, VisualColumn>()
  for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
    for (const c of g.COLUMNS || []) {
      // Copied VERBATIM, groupName/StandardName included. They used to be
      // stripped here as "layout-only", but the app server's own designer
      // (`generateSQLSeq()`) rebuilds this list by pushing each group's
      // columns in unchanged — so every schema visual it writes carries the
      // group keys on the master copies too, and a platform-written row that
      // drops them is the odd one out on the server.
      if (!seen.has(c.COLID)) seen.set(c.COLID, { ...c })
    }
  }
  m.SQLMODEL.SQLModel.COLUMNS = [{ COLUMN: Array.from(seen.values()) }]
}
